'use client';

import { generateICS, downloadBlob, getGoogleCalendarUrl } from '@/lib/calendar-export';
import type { CalendarEvent } from '@/lib/types';
import { uiFb } from '@/lib/i18n';
import { useEventTranslation } from '@/components/EventTranslatedContent';
import { useState, useRef, useEffect } from 'react';

interface AddToCalendarButtonProps {
  event: CalendarEvent;
  lang?: string;
  label: string;
}

export function AddToCalendarButton({ event, lang, label }: AddToCalendarButtonProps) {
  const t = useEventTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleICS = () => {
    const calOpts = { lang, translatedTitle: t.title };
    const ics = generateICS([event], t.title, event.country, calOpts);
    const filename = (t.title || event.title).replace(/\s+/g, '-');
    downloadBlob(ics, `${filename}.ics`, 'text/calendar');
    window._paq?.push(['trackEvent', 'Export', 'SingleEventICS', event.id]);
    setOpen(false);
  };

  const handleGoogle = () => {
    const calOpts = { lang, translatedTitle: t.title };
    window.open(getGoogleCalendarUrl(event, calOpts), '_blank', 'noopener');
    window._paq?.push(['trackEvent', 'Export', 'SingleEventGoogle', event.id]);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-text-on-brand shadow-sm transition-colors hover:bg-brand/90 min-h-[44px]"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clipRule="evenodd" />
        </svg>
        {label}
      </button>
      {open && (
        <div className="absolute start-0 top-full z-10 mt-1 w-56 content-card py-1 shadow-lg">
          <button
            onClick={handleICS}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-text-primary hover:bg-surface-hover min-h-[44px]"
          >
            <svg className="h-4 w-4 text-text-secondary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
              <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
            </svg>
            {uiFb('ui.detail.download_ics', 'Download .ics file', lang)}
          </button>
          <button
            onClick={handleGoogle}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-text-primary hover:bg-surface-hover min-h-[44px]"
          >
            <svg className="h-4 w-4 text-text-secondary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
            </svg>
            {uiFb('ui.detail.google_calendar', 'Google Calendar', lang)}
          </button>
        </div>
      )}
    </div>
  );
}
